// Where a keeper buys a supply (feedback #65, 2026-10-07: "doesn't save
// where you buy it"). Two things a keeper may type, kept apart:
//   - the store's name ("Bulk Reef Supply", "my LFS"), free text;
//   - a product link. Keepers type "bulkreefsupply.com/..." without the
//     https://, and until this the server refused it with a 400 the app
//     never showed, or dropped it silently on a new supply.
// Pure, shared by the app and the server.

export const STORE_NAME_MAX = 80;
const LINK_MAX = 500;

export const REORDER_LINK_ERROR =
  "That isn't a web address. Put the store's name in Where you buy it, and paste a product link (like bulkreefsupply.com/...) here.";

// Equipment parts have no store box, only the link (2026-10-08: a part's
// "bulkreefsupply.com/..." was saved as typed and its Reorder button opened
// a page inside TankSensei).
export const PART_LINK_ERROR =
  "That isn't a web address. Paste a product link (like bulkreefsupply.com/...), or leave it empty.";

// A store's front page (bulkreefsupply.com) names the store, not the part:
// Reorder would open the home page instead of searching it for the part.
export const PART_HOMEPAGE_HINT =
  "That's the store's front page. Paste the link to the part's own page, or leave it empty and Reorder searches your usual store for it.";

// A stored link, made safe to open: links saved before this check (no
// https://, a share-sheet paste) work, and anything that is not http(s)
// is never an href. Null when nothing usable is there.
export function safeReorderHref(value) {
  const link = normalizeReorderLink(value);
  return link.error ? null : link.url;
}

// { url, homepage } (url null when empty) or { error }. Only http(s)
// ever reaches an href: a javascript: or data: link is refused, not
// rewritten. A phone's share sheet pastes "Red Sea Foundation A
// https://a.co/d/abc": the one link in the text is taken. `homepage` marks
// a bare store address (amazon.com): that names the store, it is not a
// product link (storeAndLink below).
export function normalizeReorderLink(value) {
  let raw = String(value ?? '').trim();
  if (!raw) return { url: null };
  if (/\s/.test(raw)) {
    const found = raw.match(/https?:\/\/[^\s<>"']+/gi) || [];
    if (found.length !== 1) return { error: REORDER_LINK_ERROR };
    raw = found[0].replace(/[).,;!?]+$/, '');
  }
  const hasScheme =
    /^[a-z][a-z0-9+.-]*:\/\//i.test(raw) || /^(javascript|data|vbscript):/i.test(raw);
  const withScheme = hasScheme ? raw : `https://${raw}`;
  let u;
  try {
    u = new URL(withScheme);
  } catch {
    return { error: REORDER_LINK_ERROR };
  }
  if (!/^https?:$/.test(u.protocol) || !u.hostname.includes('.')) {
    return { error: REORDER_LINK_ERROR };
  }
  // A long store link is mostly tracking: the product is in the path.
  let url = withScheme;
  if (url.length > LINK_MAX) url = `${u.origin}${u.pathname}`;
  if (url.length > LINK_MAX) return { error: REORDER_LINK_ERROR };
  const homepage = (u.pathname === '/' || u.pathname === '') && !u.search && !u.hash;
  return { url, homepage };
}

// The two boxes of the supply form, read together: { store_name,
// reorder_url } or { error }. A bare store address typed as the link
// (amazon.com) would open the store's front page instead of searching it
// for the product, so it becomes the store's name when that box is empty
// and is never kept as the link.
export function storeAndLink(storeText, linkText) {
  const link = normalizeReorderLink(linkText);
  if (link.error) return { error: link.error };
  let store = cleanStoreName(storeText);
  if (link.homepage) {
    if (!store) store = new URL(link.url).hostname.replace(/^www\./i, '');
    return { store_name: store, reorder_url: null };
  }
  return { store_name: store, reorder_url: link.url };
}

// The store's name as the keeper typed it, trimmed; null when empty.
export function cleanStoreName(value) {
  if (typeof value !== 'string') return null;
  const s = value.replace(/\s+/g, ' ').trim().slice(0, STORE_NAME_MAX);
  return s || null;
}

function hostOf(url) {
  try {
    return new URL(url || '').hostname.replace(/^www\./i, '');
  } catch {
    return '';
  }
}

// The retailer in the reorder library the store's name points at, so
// Reorder searches the store the keeper actually uses. Matches the key or
// any word group of the label ("BRS / Bulk Reef Supply" answers to "brs",
// "bulk reef supply" or "Bulk Reef"). Null when nothing matches: a local
// store keeps the keeper's preferred retailer for the search.
export function retailerForStore(storeName, retailers) {
  const s = String(storeName || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
  if (!s || !Array.isArray(retailers)) return null;
  for (const r of retailers) {
    // The store's web address counts too: keepers type "bulkreefsupply.com".
    const host = hostOf(r?.search_url);
    const names = [r?.k, host, ...String(r?.label || '').split('/')]
      .map((n) =>
        String(n || '')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, ' ')
          .trim()
      )
      .filter((n) => n.length >= 3);
    // Whole words only: "Amazonia Aquatics" is a local store, not Amazon.
    if (
      names.some((n) => ` ${s} `.includes(` ${n} `) || (s.length >= 4 && n.startsWith(`${s} `)))
    ) {
      return r;
    }
  }
  return null;
}
